import FixaCarousel, {
  FixaOverflowCarousel,
} from "@coworker/apprestructured/src/shared/wrappers/FixaCarousel/FixaCarousel";
import FixaImage from "@coworker/apprestructured/src/shared/wrappers/FixaImage/FixaImage";
import styles from "./Images.module.css";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { useTranslation } from "@coworker/locales";
import { useCallback, useMemo, useState } from "react";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { ZoomableImage } from "@coworker/app/src/components/RoomSettings/Molecules/ZoomableImage";
import { FixaTheatre } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaTheatre";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import {
  isLoading,
  Loading,
} from "@coworker/apprestructured/src/task-management/loading";
import LoadingCarousel from "@coworker/apprestructured/src/task-management/components/Images/LoadingCarousel/LoadingCarousel";

type ImagesProps = {
  urls: string[] | Loading;
};

export const Images = ({ urls }: ImagesProps) => {
  const [showModal, setShowModal] = useState(false);
  const [activeSlideImageIndex, setActiveSlideImageIndex] = useState(0);

  const { t } = useTranslation();

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const handleImageClick = useCallback((index: number) => {
    setActiveSlideImageIndex(index);
    setShowModal(true);
  }, []);

  const carousel = useMemo(() => {
    if (isLoading(urls)) {
      return <LoadingCarousel />;
    }
    return (
      <FixaOverflowCarousel id="images">
        {urls.map((url, index) => {
          return (
            <FixaImage
              key={url}
              src={url}
              alt={`Image ${index + 1}`}
              className={styles["image"]}
              onClick={() => handleImageClick(index)}
            />
          );
        })}
      </FixaOverflowCarousel>
    );
  }, [handleImageClick, urls]);

  const modal = useMemo(() => {
    if (isLoading(urls)) {
      return null;
    }
    return (
      <FixaModal visible={showModal} handleCloseBtn={handleCloseModal}>
        <FixaTheatre
          header={<FixaModalHeader />}
          ariaLabel={t("imageCarouselString")}
        >
          <FixaModalBody>
            <FixaCarousel
              id={"images"}
              isSlideShow
              slideIndex={activeSlideImageIndex}
              ariaLabelLeftBtn={t("previousImageString")}
              ariaLabelRightBtn={t("nextImageString")}
              className={styles["carousel"]}
            >
              {urls.map((url, index) => (
                <ZoomableImage key={url} src={url} alt={`Image ${index + 1}`} />
              ))}
            </FixaCarousel>
          </FixaModalBody>
        </FixaTheatre>
      </FixaModal>
    );
  }, [activeSlideImageIndex, handleCloseModal, urls, showModal, t]);

  if (!isLoading(urls) && urls.length === 0) {
    return null;
  }

  return (
    <>
      <div>
        <FixaText textStyle="Heading.S">{t("imagesString")}</FixaText>
        {carousel}
      </div>
      {modal}
    </>
  );
};
